import Cuboid from '../rendering/Cuboid.js';
import Material from '../materials/Material.js';
import CharacterSprite from './CharacterSprite.js';
import { setShowBelts, getShowBelts } from './LPCLayerDefinitions.js';

/**
 * Debug visualization for character collision boxes and sprite tuning
 * Shows a wireframe cuboid representing the character's AABB
 */
export default class CharacterDebugVisualizer {
  constructor(world, container = null, inputManager = null) {
    this.world = world;
    this.container = container; // Optional container to render into (e.g., sidebar)
    this.inputManager = inputManager; // Reference to input manager for joystick access
    this.debugCuboids = new Map(); // characterId -> Cuboid
    this.enabled = false; // Off by default
    this.player = null; // Reference to player character (set externally)
    this.game = null; // Reference to game instance (set externally)

    // Available characters for selection
    this.availableCharacters = [
      { id: 'boy', name: 'Boy' },
      { id: 'mage', name: 'Mage' },
      { id: 'merchant', name: 'Merchant' },
      { id: 'surfer', name: 'Surfer' },
      { id: 'bear', name: 'Bear' },
      { id: 'hellrat', name: 'Hellrat' },
      { id: 'rogue', name: 'Rogue' }
    ];
    this.currentCharacter = 'boy'; // Default character

    // Sprite offset tuning (applied to all characters)
    // Defaults tuned for gandalf character
    this.spriteOffsetX = 0;
    this.spriteOffsetY = -0.88; // Vertical offset to align feet with ground
    this.spriteOffsetZ = 0;
    this.spriteScale = 1.4; // Scale multiplier for sprite size

    // Create debug UI
    this.createDebugUI();
  }

  /**
   * Create debug material for collision box visualization
   */
  createDebugMaterial() {
    const material = Material.color('rgba(255, 255, 0, 0.2)');
    material.borderWidth = 2;
    material.borderColor = '#ffff00';
    material.borderStyle = 'dashed';
    return material;
  }

  /**
   * Update debug visualization for a character
   * @param {Character} character - The character to visualize
   */
  updateCharacter(character) {
    if (!this.enabled) {
      this.hideCharacter(character.id);
      return;
    }

    const aabb = character.getAABB();
    const x = aabb.minX;
    const y = aabb.minY;
    const z = aabb.minZ;
    const width = aabb.maxX - aabb.minX;
    const height = aabb.maxY - aabb.minY;
    const depth = aabb.maxZ - aabb.minZ;

    let cuboid = this.debugCuboids.get(character.id);

    if (cuboid) {
      // Update existing cuboid
      cuboid.x = x;
      cuboid.y = y;
      cuboid.z = z;
      cuboid.width = width;
      cuboid.height = height;
      cuboid.depth = depth;
      cuboid.needsUpdate = true;
      cuboid.update(this.world.camera);
      this.ensureInDOM(cuboid);
    } else {
      // Create new debug cuboid
      cuboid = new Cuboid({
        x, y, z,
        width, height, depth,
        cubeSize: this.world.cubeSize,
        skipAO: true, // Don't apply ambient occlusion to debug visualization
        materials: {
          top: this.createDebugMaterial(),
          bottom: this.createDebugMaterial(),
          front: this.createDebugMaterial(),
          back: this.createDebugMaterial(),
          left: this.createDebugMaterial(),
          right: this.createDebugMaterial()
        }
      });

      cuboid.element.classList.add('debug-collider');
      cuboid.element.style.pointerEvents = 'none';
      cuboid.update(this.world.camera);

      this.debugCuboids.set(character.id, cuboid);
      this.ensureInDOM(cuboid);
    }
  }

  /**
   * Ensure debug cuboid is in DOM
   */
  ensureInDOM(cuboid) {
    if (cuboid && cuboid.element.parentNode !== this.world.element) {
      this.world.element.appendChild(cuboid.element);
    }
  }

  /**
   * Hide debug visualization for a character
   */
  hideCharacter(characterId) {
    const cuboid = this.debugCuboids.get(characterId);
    if (cuboid) {
      cuboid.element.remove();
      this.debugCuboids.delete(characterId);
    }
  }

  /**
   * Update all character debug visualizations
   */
  update() {
    if (!this.enabled) return;

    for (const character of this.world.characters.values()) {
      this.updateCharacter(character);
    }
  }

  /**
   * Toggle debug visualization
   */
  toggle() {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      // Remove all debug cuboids
      for (const [id, cuboid] of this.debugCuboids) {
        cuboid.element.remove();
      }
      this.debugCuboids.clear();
    }
    this.updateUI();
  }

  /**
   * Get sprite offset for positioning
   */
  getSpriteOffset() {
    return {
      x: this.spriteOffsetX,
      y: this.spriteOffsetY,
      z: this.spriteOffsetZ,
      scale: this.spriteScale
    };
  }

  /**
   * Create debug UI panel
   */
  createDebugUI() {
    const section = document.createElement('div');
    section.id = 'characterDebugPanel';
    section.className = 'gui-section';

    section.innerHTML = `
      <div class="gui-section-header">
        <h3 class="gui-section-title">Character Debug</h3>
        <svg class="gui-section-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      <div class="gui-section-body">
        <div class="gui-subsection">
          <div class="gui-subsection-title">Character</div>
          <div class="gui-row gui-row-stacked">
            <label class="gui-label gui-label-stacked">Select Character</label>
            <select id="characterSelect" class="gui-select">
              <option value="" disabled style="display:none">— LPC Custom —</option>
              <option value="boy" selected>Boy</option>
              <option value="mage">Mage</option>
              <option value="merchant">Merchant</option>
              <option value="surfer">Surfer</option>
              <option value="bear">Bear</option>
              <option value="hellrat">Hellrat</option>
              <option value="rogue">Rogue</option>
            </select>
          </div>
        </div>

        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" id="debugColliderEnabled" class="gui-checkbox">
            <span class="gui-checkbox-label">Show Collider</span>
          </label>
        </div>

        <div class="gui-row">
          <label class="gui-checkbox-container">
            <input type="checkbox" id="debugShowBelts" class="gui-checkbox">
            <span class="gui-checkbox-label">Show Belts</span>
          </label>
          <span class="gui-hint" style="font-size:10px;color:#888;margin-left:4px;">(disabled — belt sprites lack run anims, cover near arm)</span>
        </div>

        <div class="gui-subsection">
          <div class="gui-subsection-title">Sprite Tuning</div>

          <div class="gui-row">
            <span class="gui-label">Scale</span>
            <div class="gui-slider-row">
              <input type="range" id="spriteScale" class="gui-slider" min="0.5" max="2" step="0.05" value="1.4">
              <input type="number" id="spriteScaleValue" class="gui-input gui-slider-value" value="1.40" step="0.05" min="0.5" max="2">
            </div>
          </div>

          <div class="gui-row">
            <span class="gui-label">Y (feet)</span>
            <div class="gui-slider-row">
              <input type="range" id="spriteOffsetY" class="gui-slider" min="-3" max="1" step="0.05" value="-0.88">
              <input type="number" id="spriteOffsetYValue" class="gui-input gui-slider-value" value="-0.88" step="0.05" min="-3" max="1">
            </div>
          </div>

          <div class="gui-row">
            <span class="gui-label">X</span>
            <div class="gui-slider-row">
              <input type="range" id="spriteOffsetX" class="gui-slider" min="-1" max="1" step="0.05" value="0">
              <input type="number" id="spriteOffsetXValue" class="gui-input gui-slider-value" value="0.00" step="0.05" min="-1" max="1">
            </div>
          </div>

          <div class="gui-row">
            <span class="gui-label">Z</span>
            <div class="gui-slider-row">
              <input type="range" id="spriteOffsetZ" class="gui-slider" min="-1" max="1" step="0.05" value="0">
              <input type="number" id="spriteOffsetZValue" class="gui-input gui-slider-value" value="0.00" step="0.05" min="-1" max="1">
            </div>
          </div>
        </div>

        <div class="gui-subsection">
          <div class="gui-subsection-title">Death Animation</div>

          <div class="gui-row">
            <span class="gui-label">Drop</span>
            <div class="gui-slider-row">
              <input type="range" id="deathDrop" class="gui-slider" min="0" max="0.8" step="0.01" value="0.2">
              <input type="number" id="deathDropValue" class="gui-input gui-slider-value" value="0.20" step="0.01" min="0" max="0.8">
            </div>
          </div>
        </div>

        <div class="gui-subsection">
          <div class="gui-subsection-title">Shadow</div>

          <div class="gui-row">
            <span class="gui-label">Radius</span>
            <div class="gui-slider-row">
              <input type="range" id="shadowRadius" class="gui-slider" min="0.1" max="1.5" step="0.05" value="0.5">
              <input type="number" id="shadowRadiusValue" class="gui-input gui-slider-value" value="0.50" step="0.05" min="0.1" max="1.5">
            </div>
          </div>

          <div class="gui-row">
            <span class="gui-label">Fade Dist</span>
            <div class="gui-slider-row">
              <input type="range" id="shadowMaxDistance" class="gui-slider" min="1" max="10" step="0.5" value="3">
              <input type="number" id="shadowMaxDistanceValue" class="gui-input gui-slider-value" value="3.0" step="0.5" min="1" max="10">
            </div>
          </div>

          <div class="gui-row">
            <span class="gui-label">Opacity</span>
            <div class="gui-slider-row">
              <input type="range" id="shadowMaxOpacity" class="gui-slider" min="0.1" max="0.8" step="0.05" value="0.4">
              <input type="number" id="shadowMaxOpacityValue" class="gui-input gui-slider-value" value="0.40" step="0.05" min="0.1" max="0.8">
            </div>
          </div>
        </div>

        <div class="gui-subsection">
          <div class="gui-subsection-title">Sprite AO</div>

          <div class="gui-row">
            <span class="gui-label">Lerp Speed</span>
            <div class="gui-slider-row">
              <input type="range" id="spriteAoLerp" class="gui-slider" min="0.01" max="0.2" step="0.01" value="0.08">
              <input type="number" id="spriteAoLerpValue" class="gui-input gui-slider-value" value="0.08" step="0.01" min="0.01" max="0.2">
            </div>
          </div>

          <div class="gui-row">
            <span class="gui-label">Max Distance</span>
            <div class="gui-slider-row">
              <input type="range" id="spriteAoMaxDist" class="gui-slider" min="0" max="2" step="0.1" value="0.5">
              <input type="number" id="spriteAoMaxDistValue" class="gui-input gui-slider-value" value="0.50" step="0.1" min="0" max="2">
            </div>
          </div>
        </div>

        <div class="gui-subsection">
          <div class="gui-subsection-title">Joystick</div>

          <div class="gui-row">
            <label class="gui-checkbox-container">
              <input type="checkbox" id="joystickFixedMode" class="gui-checkbox" checked>
              <span class="gui-checkbox-label">Fixed Mode</span>
            </label>
          </div>

          <div class="gui-row">
            <label class="gui-checkbox-container">
              <input type="checkbox" id="joystickZoneDebug" class="gui-checkbox">
              <span class="gui-checkbox-label">Show Zone</span>
            </label>
          </div>

          <div class="gui-row">
            <span class="gui-label">Width</span>
            <div class="gui-slider-row">
              <input type="range" id="joystickZoneWidth" class="gui-slider" min="100" max="500" step="10" value="200">
              <input type="number" id="joystickZoneWidthValue" class="gui-input gui-slider-value" value="200" step="10" min="100" max="500">
            </div>
          </div>

          <div class="gui-row">
            <span class="gui-label">Height</span>
            <div class="gui-slider-row">
              <input type="range" id="joystickZoneHeight" class="gui-slider" min="100" max="600" step="10" value="370">
              <input type="number" id="joystickZoneHeightValue" class="gui-input gui-slider-value" value="370" step="10" min="100" max="600">
            </div>
          </div>
        </div>

        <div class="gui-subsection">
          <div class="gui-subsection-title">Debug Info</div>
          <div id="characterDebugInfo" class="gui-info"></div>
          <div id="pathDebugInfo" class="gui-info" style="font-size: 11px;"></div>
        </div>
      </div>
    `;

    // Append to container if provided, otherwise to body
    if (this.container) {
      this.container.appendChild(section);
    } else {
      document.body.appendChild(section);
    }
    this.panel = section;

    // Add collapse functionality
    const header = section.querySelector('.gui-section-header');
    header.addEventListener('click', () => {
      section.classList.toggle('collapsed');
    });

    // Character selector
    document.getElementById('characterSelect').addEventListener('change', (e) => {
      const characterId = e.target.value;
      this.currentCharacter = characterId;
      if (this.game && this.game.switchCharacter) {
        this.game.switchCharacter(characterId);
      }
      // Sync death drop slider to selected character's value
      const ddSlider = document.getElementById('deathDrop');
      const ddValue = document.getElementById('deathDropValue');
      const ddCurrent = CharacterSprite.DEATH_DROP[characterId] ?? CharacterSprite.DEATH_DROP_DEFAULT;
      if (ddSlider) ddSlider.value = ddCurrent;
      if (ddValue) ddValue.value = ddCurrent.toFixed(2);
    });

    // Collider toggle
    document.getElementById('debugColliderEnabled').addEventListener('change', (e) => {
      this.enabled = e.target.checked;
      if (!this.enabled) {
        for (const [id, cuboid] of this.debugCuboids) {
          cuboid.element.remove();
        }
        this.debugCuboids.clear();
      }
    });

    // Belt display toggle — refreshes all LPC character sprites
    document.getElementById('debugShowBelts').addEventListener('change', (e) => {
      setShowBelts(e.target.checked);
      if (this.game && this.game.inventory) {
        this.game.inventory.onChange(this.game.inventory);
      }
    });

    // Sprite scale - sync slider and number input
    const spriteScaleSlider = document.getElementById('spriteScale');
    const spriteScaleValue = document.getElementById('spriteScaleValue');
    spriteScaleSlider.addEventListener('input', (e) => {
      this.spriteScale = parseFloat(e.target.value);
      spriteScaleValue.value = this.spriteScale.toFixed(2);
    });
    spriteScaleValue.addEventListener('input', (e) => {
      this.spriteScale = parseFloat(e.target.value) || 1;
      spriteScaleSlider.value = this.spriteScale;
    });

    // Sprite Y offset
    const spriteOffsetYSlider = document.getElementById('spriteOffsetY');
    const spriteOffsetYValue = document.getElementById('spriteOffsetYValue');
    spriteOffsetYSlider.addEventListener('input', (e) => {
      this.spriteOffsetY = parseFloat(e.target.value);
      spriteOffsetYValue.value = this.spriteOffsetY.toFixed(2);
    });
    spriteOffsetYValue.addEventListener('input', (e) => {
      this.spriteOffsetY = parseFloat(e.target.value) || 0;
      spriteOffsetYSlider.value = this.spriteOffsetY;
    });

    // Sprite X offset
    const spriteOffsetXSlider = document.getElementById('spriteOffsetX');
    const spriteOffsetXValue = document.getElementById('spriteOffsetXValue');
    spriteOffsetXSlider.addEventListener('input', (e) => {
      this.spriteOffsetX = parseFloat(e.target.value);
      spriteOffsetXValue.value = this.spriteOffsetX.toFixed(2);
    });
    spriteOffsetXValue.addEventListener('input', (e) => {
      this.spriteOffsetX = parseFloat(e.target.value) || 0;
      spriteOffsetXSlider.value = this.spriteOffsetX;
    });

    // Sprite Z offset
    const spriteOffsetZSlider = document.getElementById('spriteOffsetZ');
    const spriteOffsetZValue = document.getElementById('spriteOffsetZValue');
    spriteOffsetZSlider.addEventListener('input', (e) => {
      this.spriteOffsetZ = parseFloat(e.target.value);
      spriteOffsetZValue.value = this.spriteOffsetZ.toFixed(2);
    });
    spriteOffsetZValue.addEventListener('input', (e) => {
      this.spriteOffsetZ = parseFloat(e.target.value) || 0;
      spriteOffsetZSlider.value = this.spriteOffsetZ;
    });

    // Death drop multiplier (per-character)
    const deathDropSlider = document.getElementById('deathDrop');
    const deathDropValue = document.getElementById('deathDropValue');
    deathDropSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      deathDropValue.value = val.toFixed(2);
      CharacterSprite.DEATH_DROP[this.currentCharacter] = val;
    });
    deathDropValue.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value) || 0.35;
      deathDropSlider.value = val;
      CharacterSprite.DEATH_DROP[this.currentCharacter] = val;
    });

    // Shadow radius
    const shadowRadiusSlider = document.getElementById('shadowRadius');
    const shadowRadiusValue = document.getElementById('shadowRadiusValue');
    shadowRadiusSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      shadowRadiusValue.value = val.toFixed(2);
      if (this.player?.shadow) this.player.shadow.radius = val;
    });
    shadowRadiusValue.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value) || 0.5;
      shadowRadiusSlider.value = val;
      if (this.player?.shadow) this.player.shadow.radius = val;
    });

    // Shadow max distance (fade distance)
    const shadowMaxDistanceSlider = document.getElementById('shadowMaxDistance');
    const shadowMaxDistanceValue = document.getElementById('shadowMaxDistanceValue');
    shadowMaxDistanceSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      shadowMaxDistanceValue.value = val.toFixed(1);
      if (this.player?.shadow) this.player.shadow.maxDistance = val;
    });
    shadowMaxDistanceValue.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value) || 3;
      shadowMaxDistanceSlider.value = val;
      if (this.player?.shadow) this.player.shadow.maxDistance = val;
    });

    // Shadow max opacity
    const shadowMaxOpacitySlider = document.getElementById('shadowMaxOpacity');
    const shadowMaxOpacityValue = document.getElementById('shadowMaxOpacityValue');
    shadowMaxOpacitySlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      shadowMaxOpacityValue.value = val.toFixed(2);
      if (this.player?.shadow) this.player.shadow.maxOpacity = val;
    });
    shadowMaxOpacityValue.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value) || 0.4;
      shadowMaxOpacitySlider.value = val;
      if (this.player?.shadow) this.player.shadow.maxOpacity = val;
    });

    // Sprite AO lerp speed
    const spriteAoLerpSlider = document.getElementById('spriteAoLerp');
    const spriteAoLerpValue = document.getElementById('spriteAoLerpValue');
    spriteAoLerpSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      spriteAoLerpValue.value = val.toFixed(2);
      CharacterSprite.AO_LERP_SPEED = val;
    });
    spriteAoLerpValue.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value) || 0.08;
      spriteAoLerpSlider.value = val;
      CharacterSprite.AO_LERP_SPEED = val;
    });

    // Sprite AO max distance
    const spriteAoMaxDistSlider = document.getElementById('spriteAoMaxDist');
    const spriteAoMaxDistValue = document.getElementById('spriteAoMaxDistValue');
    spriteAoMaxDistSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      spriteAoMaxDistValue.value = val.toFixed(2);
      CharacterSprite.AO_MAX_DISTANCE = val;
    });
    spriteAoMaxDistValue.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value) || 0.5;
      spriteAoMaxDistSlider.value = val;
      CharacterSprite.AO_MAX_DISTANCE = val;
    });

    // Joystick fixed mode toggle
    document.getElementById('joystickFixedMode').addEventListener('change', (e) => {
      const joystick = this.inputManager?.virtualJoystick;
      if (joystick) {
        joystick.setFixedMode(e.target.checked);
      }
    });

    // Joystick zone debug toggle
    document.getElementById('joystickZoneDebug').addEventListener('change', (e) => {
      const joystick = this.inputManager?.virtualJoystick;
      if (joystick) {
        joystick.setDebugVisible(e.target.checked);
      }
    });

    // Joystick zone width
    const joystickWidthSlider = document.getElementById('joystickZoneWidth');
    const joystickWidthValue = document.getElementById('joystickZoneWidthValue');
    joystickWidthSlider.addEventListener('input', (e) => {
      const val = parseInt(e.target.value);
      joystickWidthValue.value = val;
      const joystick = this.inputManager?.virtualJoystick;
      if (joystick) {
        joystick.setZoneDimensions(val, joystick.zoneHeight);
      }
    });
    joystickWidthValue.addEventListener('input', (e) => {
      const val = parseInt(e.target.value) || 250;
      joystickWidthSlider.value = val;
      const joystick = this.inputManager?.virtualJoystick;
      if (joystick) {
        joystick.setZoneDimensions(val, joystick.zoneHeight);
      }
    });

    // Joystick zone height
    const joystickHeightSlider = document.getElementById('joystickZoneHeight');
    const joystickHeightValue = document.getElementById('joystickZoneHeightValue');
    joystickHeightSlider.addEventListener('input', (e) => {
      const val = parseInt(e.target.value);
      joystickHeightValue.value = val;
      const joystick = this.inputManager?.virtualJoystick;
      if (joystick) {
        joystick.setZoneDimensions(joystick.zoneWidth, val);
      }
    });
    joystickHeightValue.addEventListener('input', (e) => {
      const val = parseInt(e.target.value) || 370;
      joystickHeightSlider.value = val;
      const joystick = this.inputManager?.virtualJoystick;
      if (joystick) {
        joystick.setZoneDimensions(joystick.zoneWidth, val);
      }
    });
  }

  /**
   * Sync UI sliders/inputs with current offset values (after character switch)
   */
  syncSlidersFromValues() {
    const scaleSlider = document.getElementById('spriteScale');
    const scaleValue = document.getElementById('spriteScaleValue');
    if (scaleSlider) scaleSlider.value = this.spriteScale;
    if (scaleValue) scaleValue.value = this.spriteScale.toFixed(2);

    const ySlider = document.getElementById('spriteOffsetY');
    const yValue = document.getElementById('spriteOffsetYValue');
    if (ySlider) ySlider.value = this.spriteOffsetY;
    if (yValue) yValue.value = this.spriteOffsetY.toFixed(2);

    const xSlider = document.getElementById('spriteOffsetX');
    const xValue = document.getElementById('spriteOffsetXValue');
    if (xSlider) xSlider.value = this.spriteOffsetX;
    if (xValue) xValue.value = this.spriteOffsetX.toFixed(2);

    const zSlider = document.getElementById('spriteOffsetZ');
    const zValue = document.getElementById('spriteOffsetZValue');
    if (zSlider) zSlider.value = this.spriteOffsetZ;
    if (zValue) zValue.value = this.spriteOffsetZ.toFixed(2);

    const ddSlider = document.getElementById('deathDrop');
    const ddValue = document.getElementById('deathDropValue');
    const ddCurrent = CharacterSprite.DEATH_DROP[this.currentCharacter] ?? CharacterSprite.DEATH_DROP_DEFAULT;
    if (ddSlider) ddSlider.value = ddCurrent;
    if (ddValue) ddValue.value = ddCurrent.toFixed(2);
  }

  /**
   * Sync the character dropdown to reflect the current character.
   * When an LPC skin is active, shows the placeholder so all legacy options remain selectable.
   * @param {string|null} characterName - Legacy character id, or null for LPC custom
   */
  syncCharacterDropdown(characterName) {
    const select = document.getElementById('characterSelect');
    if (!select) return;
    if (characterName && this.availableCharacters.some(c => c.id === characterName)) {
      select.value = characterName;
      this.currentCharacter = characterName;
    } else {
      // LPC custom — show the disabled placeholder
      select.value = '';
      this.currentCharacter = '';
    }
  }

  /**
   * Update debug info display
   */
  updateDebugInfo(character) {
    const info = document.getElementById('characterDebugInfo');
    if (info && character) {
      info.innerHTML = `
        Pos: (${character.x.toFixed(2)}, ${character.y.toFixed(2)}, ${character.z.toFixed(2)})<br>
        Grounded: ${character.isGrounded}<br>
        Velocity: (${character.velocity.x.toFixed(2)}, ${character.velocity.y.toFixed(2)}, ${character.velocity.z.toFixed(2)})
      `;
    }

    // Update pathfinding debug info
    const pathInfo = document.getElementById('pathDebugInfo');
    if (pathInfo && character && character.pathFollower) {
      const pf = character.pathFollower;
      if (pf.isFollowing && pf.path) {
        pathInfo.innerHTML = `
          Following: ${pf.waypointIndex + 1}/${pf.path.length}<br>
          Distance: ${pf.getRemainingDistance().toFixed(1)}
        `;
      } else {
        pathInfo.innerHTML = 'Not following path';
      }
    }
  }

  /**
   * Update UI state
   */
  updateUI() {
    const checkbox = document.getElementById('debugColliderEnabled');
    if (checkbox) {
      checkbox.checked = this.enabled;
    }
  }

  /**
   * Clean up
   */
  destroy() {
    for (const [id, cuboid] of this.debugCuboids) {
      cuboid.element.remove();
    }
    this.debugCuboids.clear();
    if (this.panel) {
      this.panel.remove();
    }
  }
}
